import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

import analytics from "@/analytics";
import { notify } from "@/components/shared/AppToast";
import { t, tn } from "@/localization";
import { fetchFileLink, fetchLibrary, requestRecording } from "./api";
import { createDownloadController } from "./downloads";
import {
  IDLE,
  MAX_PREPARING,
  PAGE_SIZE,
  normalizeRecording,
  recordingTitle,
  searchRecordings,
  sortRecordings,
} from "./logic";
import openDownload from "./openDownload";

// State behind Download everything: the account's recordings (loaded a page
// at a time from evt.live), search and "Show more", each recording's download
// (components/library/downloads.js) and the data files.
//
// Recordings being prepared are kept on the device per account, so leaving
// and coming back keeps checking instead of starting the build again.

const pendingKey = (owner) =>
  `eventlive.library.preparing.v1.${encodeURIComponent(owner || "")}`;

export default function useLibrary(owner) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState(1);
  const [downloads, setDownloads] = useState({});
  const [files, setFiles] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const mounted = useRef(true);
  const controller = useRef(null);

  // One controller per account while the screen is open.
  useEffect(() => {
    mounted.current = true;
    if (!owner) return undefined;
    const c = createDownloadController({
      requestRecording,
      open: openDownload,
      onChange: (states) => mounted.current && setDownloads(states),
      onReady: (rec) =>
        notify.success(t("Ready to download"), recordingTitle(rec)),
      onBusy: () =>
        notify.info(
          tn(
            "{count} recording is being prepared",
            "{count} recordings are being prepared",
            MAX_PREPARING
          ),
          t("Download this one when one of them is ready.")
        ),
      load: async () => {
        try {
          const raw = await AsyncStorage.getItem(pendingKey(owner));
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      },
      save: (pending) =>
        AsyncStorage.setItem(pendingKey(owner), JSON.stringify(pending)).catch(
          () => {}
        ),
      track: (name, props) => analytics.track(name, props),
    });
    controller.current = c;
    return () => {
      mounted.current = false;
      c.dispose();
      controller.current = null;
    };
  }, [owner]);

  // ---- The list --------------------------------------------------------------

  useEffect(() => {
    if (!owner) return undefined;
    let cancelled = false;
    setLoading(true);
    setComplete(false);
    setError(null);
    fetchLibrary({
      onPage: (raw) => {
        if (cancelled) return;
        setRecordings(sortRecordings(raw.map(normalizeRecording)));
        setLoading(false);
      },
    })
      .then((raw) => {
        if (cancelled) return;
        const list = sortRecordings(raw.map(normalizeRecording));
        setRecordings(list);
        setComplete(true);
        setLoading(false);
        controller.current?.resume(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || t("Could not load your recordings."));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [owner, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const filtered = useMemo(
    () => searchRecordings(recordings, query),
    [recordings, query]
  );
  const visible = useMemo(
    () => filtered.slice(0, pages * PAGE_SIZE),
    [filtered, pages]
  );
  const search = useCallback((text) => {
    setQuery(text);
    setPages(1);
  }, []);
  const showMore = useCallback(() => setPages((p) => p + 1), []);

  const download = useCallback((rec) => controller.current?.download(rec), []);

  // After an archived recording's event was deleted: drop its row.
  const remove = useCallback((slug) => {
    setRecordings((list) => list.filter((r) => r.slug !== slug));
  }, []);

  // ---- Data files ------------------------------------------------------------

  const openFile = useCallback(async (file, slug) => {
    const key = slug ? `${file}:${slug}` : file;
    setFiles((all) => ({ ...all, [key]: { status: "checking" } }));
    try {
      const { url } = await fetchFileLink(file, slug);
      if (!url) throw new Error();
      analytics.track("Library File Downloaded", { file });
      openDownload(url);
    } catch (e) {
      if (mounted.current)
        notify.error(
          t("Couldn't download the file"),
          e?.message || t("Please try again.")
        );
    } finally {
      if (mounted.current) setFiles((all) => ({ ...all, [key]: IDLE }));
    }
  }, []);

  return {
    loading,
    complete,
    error,
    reload,
    total: recordings.length,
    query,
    search,
    filteredCount: filtered.length,
    visible,
    hasMore: visible.length < filtered.length,
    showMore,
    downloads,
    download,
    remove,
    files,
    openFile,
  };
}
